import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message"; // Bilgilendirme için
import { IntegrationQueryTemplate } from "../../types/integration";

interface IntegrationQueryFormProps {
    visible: boolean;
    onHide: () => void;
    onSave: (data: any) => void;
    initialData: IntegrationQueryTemplate | null; 
}

interface FormData {
    name: string;
    targetEndpoint: string;
    templateSQL: string;
    detailTemplateSQL: string; // <-- EKLENDİ
    isActive: boolean;
}

const initialFormState: FormData = {
    name: '',
    targetEndpoint: '',
    templateSQL: '',
    detailTemplateSQL: '', // <-- EKLENDİ
    isActive: true,
};

export const IntegrationQueryForm: React.FC<IntegrationQueryFormProps> = ({ 
    visible, 
    onHide, 
    onSave, 
    initialData 
}) => {
    const [formData, setFormData] = useState<FormData>(initialFormState);
    const [errors, setErrors] = useState<Partial<FormData>>({});
    const [isSaving, setIsSaving] = useState(false); 

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                targetEndpoint: initialData.targetEndpoint,
                templateSQL: initialData.templateSQL,
                detailTemplateSQL: initialData.detailTemplateSQL || '', // <-- GÜNCELLENDİ
                isActive: initialData.isActive,
            });
        } else {
            setFormData(initialFormState);
        }
        setErrors({}); 
    }, [initialData, visible]); 

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name as keyof FormData]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };
    
    const handleSwitchChange = (value: boolean) => {
        setFormData(prev => ({ ...prev, isActive: value }));
    };

    const validate = (data: FormData): Partial<FormData> => {
        let currentErrors: Partial<FormData> = {};
        if (!data.name) currentErrors.name = "Sorgu Adı zorunludur.";
        if (!data.targetEndpoint) currentErrors.targetEndpoint = "Hedef Endpoint zorunludur.";
        if (!data.templateSQL) currentErrors.templateSQL = "SQL Şablonu zorunludur.";
        return currentErrors;
    };

    const handleSubmit = async () => {
        const validationErrors = validate(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSaving(true);
        try {
            const dataToSave = initialData 
                ? { id: initialData.id, ...formData } 
                : formData;
            await onSave(dataToSave);
        } catch (error) {
            console.error("Kaydetme hatası:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const footerContent = (
        <div className="flex justify-end gap-2 mt-4">
            <Button label="İptal" icon="pi pi-times" onClick={onHide} className="p-button-text p-button-secondary" disabled={isSaving} />
            <Button label="Kaydet" icon="pi pi-check" onClick={handleSubmit} loading={isSaving} />
        </div>
    );

    return (
        <Dialog 
            header={initialData ? "Sorgu Şablonunu Düzenle" : "Yeni Sorgu Şablonu Ekle"}
            visible={visible}
            onHide={onHide}
            modal
            maximizable
            className="w-11/12 md:w-8/12 lg:w-6/12" // Biraz genişlettik çünkü 2 SQL alanı oldu
            footer={footerContent}
        >
            <div className="p-fluid grid formgrid p-2">
                
                {/* Bilgilendirme Mesajı */}
                <div className="col-12 mb-3">
                    <Message 
                        severity="info" 
                        text="Yer tutucular: $FirmId (Firma ID), $LastSyncDate (Son Senkronizasyon), $ParentGuid (Alt sorgu için ana kayıt ID)" 
                        className="w-full justify-start"
                    />
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="name" className="font-semibold">Sorgu Adı (Örn: account_movements)</label>
                    <InputText id="name" name="name" value={formData.name} onChange={handleChange} className={errors.name ? 'p-invalid' : ''} />
                    {errors.name && <small className="p-error">{errors.name}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label htmlFor="targetEndpoint" className="font-semibold">Hedef Endpoint</label>
                    <InputText id="targetEndpoint" name="targetEndpoint" value={formData.targetEndpoint} onChange={handleChange} placeholder="/sync/account-movements" className={errors.targetEndpoint ? 'p-invalid' : ''} />
                    {errors.targetEndpoint && <small className="p-error">{errors.targetEndpoint}</small>}
                </div>

                <div className="field col-12">
                    <label htmlFor="templateSQL" className="font-semibold text-blue-600">Ana SQL Şablonu</label>
                    <InputTextarea id="templateSQL" name="templateSQL" value={formData.templateSQL} onChange={handleChange} rows={5} className={errors.templateSQL ? 'p-invalid' : ''} />
                    {errors.templateSQL && <small className="p-error">{errors.templateSQL}</small>}
                </div>

                {/* YENİ ALAN: ALT SORGU */}
                <div className="field col-12">
                    <label htmlFor="detailTemplateSQL" className="font-semibold text-green-600">Alt Sorgu Şablonu (Opsiyonel - Örn: Fatura Kalemleri)</label>
                    <InputTextarea 
                        id="detailTemplateSQL" 
                        name="detailTemplateSQL" 
                        value={formData.detailTemplateSQL} 
                        onChange={handleChange} 
                        rows={5} 
                        placeholder="SELECT * FROM STOK_HAREKETLERI WHERE sth_fat_uid = $ParentGuid"
                    />
                    <small className="text-gray-500 italic">Eğer bu sorgu bir fatura/sipariş ise kalemlerini çekmek için burayı doldurun.</small>
                </div>

                <div className="field col-12 flex items-center gap-3">
                    <InputSwitch id="isActive" checked={formData.isActive} onChange={(e) => handleSwitchChange(e.value as boolean)} />
                    <label htmlFor="isActive" className="font-semibold">Sorgu Aktif</label>
                </div>
            </div>
        </Dialog>
    );
};